import React, { useEffect, useState, useCallback } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Modal, 
  Form, 
  InputNumber, 
  Input, 
  Space, 
  Popconfirm, 
  message, 
  Switch,
  Typography 
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import { vehicleCapacityTonsApi } from '../services/api';
import { VehicleCapacityTons, CreateVehicleCapacityTons } from '../types';
import { useLanguage } from '../contexts/LanguageContext';

const { Title } = Typography;

const VehicleCapacityTonsPage: React.FC = () => {
  const { t } = useLanguage();
  const [capacityTons, setCapacityTons] = useState<VehicleCapacityTons[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState<VehicleCapacityTons | null>(null);
  const [form] = Form.useForm();

  const fetchCapacityTons = useCallback(async () => {
    try {
      setLoading(true);
      const response = await vehicleCapacityTonsApi.getAllIncludeInactive();
      setCapacityTons(response.data);
    } catch (error) {
      console.error('Error fetching capacity tons:', error);
      message.error(t('error.fetching.data'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchCapacityTons();
  }, [fetchCapacityTons]);

  const handleAdd = () => {
    setEditingItem(null);
    form.resetFields();
    form.setFieldsValue({ isActive: true });
    setModalVisible(true);
  };

  const handleEdit = (item: VehicleCapacityTons) => {
    setEditingItem(item);
    form.setFieldsValue({
      capacity: item.capacity,
      description: item.description,
      isActive: item.isActive
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await vehicleCapacityTonsApi.delete(id);
      message.success(t('vehicle.deleted.successfully'));
      fetchCapacityTons();
    } catch (error) {
      console.error('Error deleting capacity tons:', error);
      message.error(t('error.deleting.vehicle'));
    }
  };

  const handleSubmit = async (values: CreateVehicleCapacityTons) => {
    try {
      if (editingItem) {
        await vehicleCapacityTonsApi.update(editingItem.id, values);
        message.success(t('vehicle.updated.successfully'));
      } else {
        await vehicleCapacityTonsApi.create(values);
        message.success(t('vehicle.created.successfully'));
      }
      
      setModalVisible(false);
      form.resetFields();
      fetchCapacityTons();
    } catch (error) {
      console.error(`Error ${editingItem ? 'updating' : 'creating'} capacity tons:`, error);
      message.error(t(`error.${editingItem ? 'updating' : 'creating'}.vehicle`));
    }
  };

  const columns: ColumnsType<VehicleCapacityTons> = [
    {
      title: t('capacity.tons'),
      dataIndex: 'capacity',
      key: 'capacity',
      sorter: (a, b) => a.capacity - b.capacity,
      render: (value) => `${value} ${t('capacity.tons')}`
    },
    {
      title: t('description'),
      dataIndex: 'description',
      key: 'description',
      render: (value) => value || '-'
    },
    {
      title: t('status'),
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive) => (
        <span style={{ color: isActive ? 'green' : 'red' }}>
          {isActive ? t('active') : t('inactive')}
        </span>
      ),
      filters: [
        { text: t('active'), value: true },
        { text: t('inactive'), value: false }
      ],
      onFilter: (value, record) => record.isActive === value
    },
    {
      title: t('created.by'),
      dataIndex: 'createdByUserName',
      key: 'createdByUserName'
    },
    {
      title: t('date'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (value) => new Date(value).toLocaleDateString()
    },
    {
      title: t('actions'),
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          />
          <Popconfirm
            title={t('delete.confirm')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('yes')}
            cancelText={t('no')}
          >
            <Button
              icon={<DeleteOutlined />}
              danger
              size="small"
            />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={2}>{t('vehicle.capacity.tons.management')}</Title>
          <Space>
            <Link to="/vehicles">
              <Button
                type="default"
                icon={<ArrowLeftOutlined />}
              >
                {t('vehicle.management')}
              </Button>
            </Link>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
            >
              {t('add.vehicle.capacity')}
            </Button>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={capacityTons}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          scroll={{ x: true }}
        />

        <Modal
          title={editingItem ? t('edit.vehicle') : t('add.vehicle')}
          open={modalVisible}
          onCancel={() => {
            setModalVisible(false);
            form.resetFields();
          }}
          onOk={() => form.submit()}
          width={600}
        >
          <Form
            form={form}
            layout="horizontal"
            labelCol={{ span: 6 }}
            wrapperCol={{ span: 18 }}
            onFinish={handleSubmit}
          >
            <Form.Item
              label={t('capacity.tons')}
              name="capacity"
              rules={[
                { required: true, message: t('required.field') },
                { type: 'number', min: 0.1, message: t('capacity.tons') + ' > 0' }
              ]}
            >
              <InputNumber
                min={0.1}
                step={0.1}
                precision={1}
                style={{ width: '100%' }}
                placeholder="e.g., 5.0"
              />
            </Form.Item>

            <Form.Item
              label={t('description')}
              name="description"
            >
              <Input.TextArea
                rows={3}
                placeholder={t('description')}
              />
            </Form.Item>

            <Form.Item
              label={t('status')}
              name="isActive"
              valuePropName="checked"
            >
              <Switch checkedChildren={t('active')} unCheckedChildren={t('inactive')} />
            </Form.Item>
          </Form>
        </Modal>
      </Card>
    </div>
  );
};

export default VehicleCapacityTonsPage;